import type {
	ICestItem,
	ICorItem,
	IMarcaItem,
	INcmItem,
	IProdutoLupaItem,
	ITamanhoItem,
} from "@/api/space/dtos/estoque-service.dto";
import type { IPessoaFuncionarioItem } from "@/api/space/dtos/pessoas-service.dto";
import type { ConfiguracaoEntidadeCatalogo } from "./catalogo.types";

/**
 * Fonte única das entidades do catálogo (lupas, selects e filtros dinâmicos).
 *
 * Tudo que descreve uma entidade mora neste arquivo. Para cadastrar uma nova:
 *
 * 1. adicione o nome em `ENTIDADES_CATALOGO`;
 * 2. adicione a configuração em `REGISTRO_ENTIDADES_CATALOGO` (o TypeScript exige,
 *    o registro é exaustivo);
 * 3. opcionalmente, tipe a linha crua em `CatalogoEntidadeDados` para que
 *    `lookups[campo].options[i].dados` venha tipado no consumidor.
 *
 * Consuma sempre pelo barril: `import { ENTIDADES_CATALOGO } from "@/configs/configCatalogo"`.
 */

// ------------------------------- CONSTANTS -------------------------------
export const ENTIDADES_CATALOGO = {
	empresas: "empresas",
	gruposEmpresa: "grupos_empresa",
	cores: "cores",
	marca: "marca",
	tamanhos: "tamanhos",
	unidades: "unidades",
	colecoes: "colecoes",
	produtos: "produtos",
	promocoes: "promocoes",
	fornecedores: "fornecedores",
	transportadoras: "transportadoras",
	representantes: "representantes",
	clientes: "clientes",
	funcionarios: "funcionarios",
	portadores: "portadores",
	etiquetas: "etiquetas",
	ncm: "ncm",
	cest: "cest",
} as const;

export const REGISTRO_ENTIDADES_CATALOGO: Record<EntidadeCatalogo, ConfiguracaoEntidadeCatalogo> = {
	empresas: {
		rota: "empresa",
		dominio: "pessoasService",
		endpointListagem: "empresa/listar-lupa",
		formatoResposta: "paginado",
		campoValor: "id",
		campoRotulo: "nomefantasia",
		campoPrefixoRotulo: "id",
		campoSubtitulo: "nome",
		campoBusca: "nome_fantasia",
		campoAtivo: "status",
		ordenarPor: "id",
	},
	grupos_empresa: {
		rota: "empresa",
		dominio: "pessoasService",
		endpointListagem: "empresa/listar-grupos",
		formatoResposta: "lista",
		campoValor: "id",
		campoRotulo: "descricao",
		campoBusca: "descricao",
		ordenarPor: "descricao",
		removerDuplicados: true,
	},
	cores: {
		rota: "cor",
		dominio: "estoqueService",
		removerDuplicados: true,
		ordenarPor: null,
		campoValor: "id",
		campoRotulo: "descricao",
		campoBusca: "descricao",
		campoBuscaNumerica: "id",
	},
	marca: {
		rota: "marca",
		dominio: "estoqueService",
		removerDuplicados: true,
		ordenarPor: "descricao",
		campoValor: "id",
		campoRotulo: "descricao",
		campoBusca: "descricao",
		campoBuscaNumerica: "id",
	},
	tamanhos: {
		rota: "tamanho",
		dominio: "estoqueService",
		removerDuplicados: true,
		// Legendas podem repetir entre grades; o value precisa ser o id real do tamanho.
		deduplicarPorRotulo: true,
		campoValor: "id",
		campoRotulo: "legenda",
		campoBusca: "legenda",
		campoBuscaNumerica: "legenda",
		ordenarPor: "ordem",
	},
	unidades: {
		rota: "unidade",
		dominio: "estoqueService",
		removerDuplicados: true,
		campoValor: "id",
		campoRotulo: "descricao",
		campoBusca: "descricao",
		campoBuscaNumerica: "id",
		ordenarPor: "descricao",
	},
	colecoes: {
		rota: "colecao",
		dominio: "estoqueService",
		removerDuplicados: true,
		campoValor: "id",
		campoRotulo: "descricao",
		campoBusca: "descricao",
		campoBuscaNumerica: "id",
		ordenarPor: "descricao",
	},
	produtos: {
		rota: "produto",
		dominio: "estoqueService",
		removerDuplicados: true,
		endpointListagem: "listar-produtos-lupa",
		campoValor: "produto_codigo",
		campoRotulo: "produto_nome",
		incluirValorNoRotulo: true,
		campoSubtitulo: "produto_cor",
		campoBusca: "produto_nome",
		camposBusca: [
			{ value: "", label: "Código/Nome" },
			{ value: "produto_referencia", label: "Referência" },
		],
		campoBuscaNumerica: "produto_codigo",
		ordenarPor: "produto_nome,produto_codigo",
	},
	promocoes: {
		rota: "promocao",
		dominio: "estoqueService",
		removerDuplicados: true,
		endpointListagem: "promocao/listar-lupa",
		campoValor: "id",
		campoRotulo: "descricao",
		campoBusca: "descricao",
		campoBuscaNumerica: "id",
		ordenarPor: "descricao,id",
	},
	fornecedores: {
		rota: "pessoa",
		dominio: "pessoasService",
		removerDuplicados: true,
		endpointListagem: "pessoa/listar",
		parametrosFixos: { fornecedor__conteudo: true, fornecedor__operador: "eq" },
		campoValor: "id",
		campoRotulo: "nome",
		campoBusca: "nome",
		campoBuscaNumerica: "id",
		ordenarPor: "nome,id",
	},
	transportadoras: {
		rota: "pessoa",
		dominio: "pessoasService",
		removerDuplicados: true,
		endpointListagem: "pessoa/listar",
		parametrosFixos: {
			transportadora__conteudo: true,
			transportadora__operador: "eq",
		},
		campoValor: "id",
		campoRotulo: "nome",
		campoBusca: "nome",
		campoBuscaNumerica: "id",
		ordenarPor: "nome,id",
	},
	representantes: {
		rota: "pessoa",
		dominio: "pessoasService",
		removerDuplicados: true,
		endpointListagem: "pessoa/listar",
		parametrosFixos: {
			representante__conteudo: true,
			representante__operador: "eq",
		},
		campoValor: "id",
		campoRotulo: "nome",
		campoBusca: "nome",
		campoBuscaNumerica: "id",
		ordenarPor: "nome,id",
	},
	clientes: {
		rota: "pessoa",
		dominio: "pessoasService",
		removerDuplicados: true,
		endpointListagem: "pessoa/listar",
		parametrosFixos: {
			cliente__conteudo: true,
			cliente__operador: "eq",
		},
		campoValor: "id",
		campoRotulo: "nome",
		campoBusca: "nome",
		campoBuscaNumerica: "id",
		ordenarPor: "nome,id",
	},
	funcionarios: {
		rota: "pessoa",
		dominio: "pessoasService",
		endpointListagem: "pessoa/listar",
		parametrosFixos: {
			funcionario__conteudo: true,
			funcionario__operador: "eq",
		},
		formatoResposta: "paginado",
		campoValor: "id",
		campoRotulo: "nome",
		campoSubtitulo: "fantasia",
		campoBusca: "nome",
		campoBuscaNumerica: "id",
		ordenarPor: "nome,id",
		removerDuplicados: true,
	},
	portadores: {
		rota: "pessoa",
		dominio: "pessoasService",
		removerDuplicados: true,
		endpointListagem: "pessoa/listar",
		parametrosFixos: {
			fornecedor__conteudo: true,
			fornecedor__operador: "eq",
			status__conteudo: "A,S,G",
			status__operador: "in",
		},
		campoValor: "id",
		campoRotulo: "nome",
		campoBusca: "nome",
		campoBuscaNumerica: "id",
		ordenarPor: "nome,id",
	},
	etiquetas: {
		rota: "etiqueta",
		dominio: "utilitarios",
		removerDuplicados: true,
		endpointListagem: "etiqueta/listar",
		campoValor: "id",
		campoRotulo: "nome",
		campoCor: "cor",
		campoBusca: "nome",
		ordenarPor: "nome",
		campoAtivo: "status",
		campoBuscaNumerica: "id",
	},
	ncm: {
		rota: "fis-ncm",
		dominio: "estoqueService",
		campoValor: "id",
		campoRotulo: "descricao",
		campoBusca: "descricao",
		campoBuscaNumerica: "id",
		preservarBuscaNumericaComoTexto: true,
		incluirValorNoRotulo: true,
		ordenarPor: "id",
	},
	cest: {
		rota: "fis-cest",
		dominio: "estoqueService",
		endpointListagem: "fis-cest/listar-por-ncm",
		campoValor: "cest",
		campoRotulo: "descricao",
		campoBusca: "descricao",
		campoBuscaNumerica: "cest",
		preservarBuscaNumericaComoTexto: true,
		incluirValorNoRotulo: true,
		ordenarPor: "cest",
	},
};

// --------------------------------- TYPES ---------------------------------
export type EntidadeCatalogo = (typeof ENTIDADES_CATALOGO)[keyof typeof ENTIDADES_CATALOGO];

/**
 * Tipo da linha crua por entidade. Entidades ausentes aqui caem em `Record<string, unknown>`.
 * Para tipar uma nova entidade, adicione uma linha: `nomeEntidade: ITipoItem;`.
 */
export interface CatalogoEntidadeDados {
	marca: IMarcaItem;
	cores: ICorItem;
	produtos: IProdutoLupaItem;
	tamanhos: ITamanhoItem;
	ncm: INcmItem;
	cest: ICestItem;
	funcionarios: IPessoaFuncionarioItem;
}

export type DadosEntidadeCatalogo<E extends string> = E extends keyof CatalogoEntidadeDados
	? CatalogoEntidadeDados[E]
	: Record<string, unknown>;

// -------------------------------- HELPERS --------------------------------
function normalizarNomeEntidadeCatalogo(nomeOuRota: string): string {
	return nomeOuRota.toLowerCase().trim();
}

// ------------------------------- FUNCTIONS -------------------------------
/** Resolve a configuração pela chave da entidade ou, como fallback, pela `rota`. */
export function obterConfiguracaoEntidadeCatalogo(nomeOuRota: string): ConfiguracaoEntidadeCatalogo {
	const entidadeNormalizada = normalizarNomeEntidadeCatalogo(nomeOuRota);
	const configuracaoPorChave = REGISTRO_ENTIDADES_CATALOGO[entidadeNormalizada as EntidadeCatalogo];
	if (configuracaoPorChave) return configuracaoPorChave;

	const configuracaoPorRota = Object.values(REGISTRO_ENTIDADES_CATALOGO).find(
		(configuracao) => configuracao.rota === entidadeNormalizada,
	);
	if (configuracaoPorRota) return configuracaoPorRota;

	return { rota: entidadeNormalizada, dominio: "estoqueService" };
}
